/**
 * OperationalBundleVerifier
 *
 * Top verification bar showing independent verification results.
 * Displays: VERIFIED/INVALID status, bundle hash, proofOnly, fixture, replay, continuity, serialization version.
 *
 * REFINEMENTS (v1.1):
 * - Calm truth-surface language ("restricted" not "proofOnly: on")
 * - Grouped verification checks with clearer labels
 * - No marketing language
 */

import React from 'react';
import type { OperationalBundleV1, BundleVerificationResult } from '../server/export/operationalBundleTypes.js';
import type { AttestationVerificationResult } from '../governanceWorkspace/attestation/operationalBundleAttestationTypes.js';
import { OperationalBundleSignatureBadge } from '../governanceWorkspace/attestation/ui/OperationalBundleSignatureBadge.js';
import { NEUTRAL, TYPOGRAPHY, SPACING } from '../modules/governanceEventStream/operationalVisualLanguage/index.js';

interface OperationalBundleVerifierProps {
  bundle: OperationalBundleV1;
  verification: BundleVerificationResult;
  attestationResult?: AttestationVerificationResult | null;
}

export const OperationalBundleVerifier: React.FC<OperationalBundleVerifierProps> = ({
  bundle,
  verification,
  attestationResult,
}) => {
  const overallColor = verification.valid ? '#5fa35f' : '#e03030';

  const badgeStyle = (bg: string, fg: string): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: 3,
    fontSize: TYPOGRAPHY.micro.size,
    fontWeight: 700,
    backgroundColor: bg,
    color: fg,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    whiteSpace: 'nowrap',
  });

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: SPACING.sp3,
      padding: `${SPACING.sp2}px ${SPACING.sp4}px`,
      backgroundColor: NEUTRAL.bg200,
      borderBottom: `1px solid ${NEUTRAL.border}`,
      minHeight: 36,
      flexWrap: 'wrap',
    }}>
      {/* Left group: status + identity */}
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sp3, flexWrap: 'wrap' }}>
        {/* Overall status */}
        <span style={badgeStyle(overallColor + '20', overallColor)}>
          {verification.valid ? 'VERIFIED' : 'INVALID'}
        </span>

        {/* Bundle hash preview */}
        <span style={{
          fontFamily: TYPOGRAPHY.mono.family,
          fontSize: TYPOGRAPHY.mono.size,
          color: NEUTRAL.text400,
        }}>
          {bundle.bundleHash.slice(0, 20)}…
        </span>
      </div>

      {/* Middle group: truth surface properties */}
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sp2, flexWrap: 'wrap' }}>
        {/* proofOnly badge — calm language */}
        <span style={badgeStyle(
          bundle.proofOnly ? '#1a1820' : '#1c1c1f',
          bundle.proofOnly ? '#807898' : '#7a7a88'
        )}>
          {bundle.proofOnly ? 'restricted' : 'full'}
        </span>

        {/* Fixture badge */}
        {bundle.fixtureIndicator && (
          <span style={badgeStyle('#251a00', '#c89010')}>fixture</span>
        )}

        {/* Replay availability */}
        <span style={badgeStyle(
          bundle.replayAvailability ? '#0c1420' : '#1c1c1f',
          bundle.replayAvailability ? '#5878a8' : '#7a7a88'
        )}>
          replay: {bundle.replayAvailability ? 'available' : 'none'}
        </span>

        {/* Continuity state */}
        <span style={badgeStyle(
          bundle.continuityState === 'stable' || bundle.continuityState === 'stabilized'
            ? '#172217'
            : bundle.continuityState === 'degraded'
            ? '#251a00'
            : '#220808',
          bundle.continuityState === 'stable' || bundle.continuityState === 'stabilized'
            ? '#5fa35f'
            : bundle.continuityState === 'degraded'
            ? '#c89010'
            : '#e03030'
        )}>
          {bundle.continuityState}
        </span>
      </div>

      {/* Serialization version */}
      <span style={{
        fontSize: TYPOGRAPHY.micro.size,
        color: NEUTRAL.text500,
        fontFamily: TYPOGRAPHY.mono.family,
      }}>
        {bundle.deterministicSerializationVersion}
      </span>

      {/* Signature badge */}
      <OperationalBundleSignatureBadge bundle={bundle} attestationResult={attestationResult} />

      {/* Right group: verification checks */}
      <div style={{ marginLeft: 'auto', display: 'flex', gap: SPACING.sp3, alignItems: 'center' }}>
        <CheckIcon pass={verification.bundleHashMatch} label="hash" />
        <CheckIcon pass={verification.paneModelHashesMatch} label="panes" />
        <CheckIcon pass={verification.proofOnlySafe} label="safe" />
        <CheckIcon pass={verification.noForbiddenFields} label="clean" />
      </div>
    </div>
  );
};

function CheckIcon({ pass, label }: { pass: boolean; label: string }) {
  return (
    <span
      title={label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 16,
        height: 16,
        borderRadius: '50%',
        fontSize: 9,
        fontWeight: 700,
        backgroundColor: pass ? '#22c55e20' : '#ef444420',
        color: pass ? '#22c55e' : '#ef4444',
      }}
    >
      {pass ? '✓' : '✗'}
    </span>
  );
}
